import { ApolloError, ApolloQueryResult, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import { equals } from 'ramda';
import { useContext, useMemo } from 'react';

import { useAuth } from '@providers/AuthProvider';
import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import authenticatedMemberInvoice from '@graphql/queries/authenticatedMemberInvoice.query.gql';
import { billTypes } from '@data/bills';

export enum PaymentStatus {
	succeeded = 'succeeded',
	canceled = 'canceled',
	pending = 'pending',
}

export enum InvoiceStatus {
	open = 'open',
	paid = 'paid',
	void = 'void',
	uncollectible = 'uncollectible',
}

export enum RefundStatus {
	refunded = 'refunded',
}

export enum IInvoiceKind {
	membership = 'membership',
	labs = 'labs',
	medications = 'medications',
	appointment = 'appointment',
}

export interface IInvoice {
	id: string;
	number: string;
	created: DateTime;
	dueDate: DateTime | null;
	paidDate: DateTime | null;
	kind: keyof typeof billTypes;
	total: number;
	status: InvoiceStatus;
	amountPaid: number;
	amountRemaining: number;
	paymentStatus: PaymentStatus;
	refundStatus: RefundStatus | null;
	hostedInvoiceUrl?: string | null;
	paymentMethod: {
		card: {
			brand: string;
			last4: string;
		};
	} | null;
	lineItems: IInvoiceLineItem[];
}

export interface IInvoiceLineItem {
	description: string;
	amount: number;
	period: {
		start: DateTime;
		end: DateTime;
	};
}

export interface IInvoiceApi {
	id: string;
	number: string;
	created: string;
	dueDate: string | null;
	paidDate: string | null;
	kind: IInvoiceKind;
	total: number;
	status: string;
	paymentStatus: string | null;
	subscriptionType: string | null; // currently not in use
	refundStatus: string | null; // currently not in use
	disputeStatus: string | null; // currently not in use
	amountPaid: number;
	amountRemaining: number;
	hostedInvoiceUrl?: string | null;
	paymentMethod: {
		card: {
			brand: string;
			last4: string;
		};
	} | null;
	lineItems:
		| {
				description: string;
				amount: number;
				period: {
					start: string;
					end: string;
				};
		  }[]
		| null;
}

interface IAuthenticatedMemberInvoice {
	authenticatedMember: {
		invoice: IInvoiceApi;
	};
}

type UseMemberInvoice = (invoiceId: string) => {
	loading: boolean;
	data?: IInvoice;
	error?: ApolloError;
	refetchMemberInvoice: () => Promise<ApolloQueryResult<IAuthenticatedMemberInvoice>>;
};

function formatLineItemPeriod(period: { start: string; end: string }) {
	return {
		start: DateTime.fromISO(period.start),
		end: DateTime.fromISO(period.end),
	};
}

const useMemberInvoice: UseMemberInvoice = (invoiceId) => {
	const { loggedIn } = useAuth();
	const { client: graphQLv2Client } = useContext(GraphQLv2Context);
	const { loading, error, data, refetch } = useQuery<IAuthenticatedMemberInvoice>(
		authenticatedMemberInvoice,
		{
			variables: { invoiceId },
			client: graphQLv2Client,
			skip: !loggedIn,
			errorPolicy: 'all',
		}
	);

	const formattedInvoice = useMemo<IInvoice | undefined>(() => {
		if (!data || !data?.authenticatedMember?.invoice) return;

		const invoice = data?.authenticatedMember?.invoice;

		const lineItems =
			invoice?.lineItems?.map<IInvoiceLineItem>((item) => ({
				...item,
				period: formatLineItemPeriod(item.period),
			})) ?? [];

		return {
			...invoice,
			lineItems,
			created: DateTime.fromISO(invoice.created),
			dueDate: invoice?.dueDate ? DateTime.fromISO(invoice.dueDate) : null,
			paymentStatus: invoice?.paymentStatus,
			refundStatus: equals(PaymentStatus.succeeded, invoice.refundStatus)
				? RefundStatus.refunded
				: null,
			paidDate: invoice?.paidDate ? DateTime.fromISO(invoice.paidDate) : null,
		};
	}, [data]);

	return {
		loading,
		data: formattedInvoice,
		error,
		refetchMemberInvoice: refetch,
	};
};

export default useMemberInvoice;
